import * as query from '@arcgis/core/rest/query';

// Store
import { api } from '../store/query/getDataRtkQuery';
import store from '../store/store';

// Utils
import { getLevelRank } from '../utils/admin2Data.utils';

// Configs
import { ATTRIBUTE_FIELD_COUNTRY_CODE, ATTRIBUTE_FIELD_SURVEY_TYPE } from '../configs/attributeFields.configs';
import { ADMIN_2_VECTOR_DATA } from '../configs/api.configs';
import {
  ADMIN_1_VECTOR_BOUNDARIES,
  ADMIN_2_VECTOR_BOUNDARIES,
  SERVICES__GEOMETRY__BASEMAP_REGIONS_TEST__MAP_SERVER__URL,
} from '../configs/map.configs';

const QUERY_PERFORMANCE_OPTIONS = {
  // geometryPrecision: 2,
  // geometryPrecision: 3, // breaks app
  geometryPrecision: 4,
};

/**
 * Get the selected country geometry from the Basemap Regions layer
 * @param {string} countryCode - Country code (e.g. 'BF')
 */
export async function getSelectedCountryGeometry(countryCode: string): Promise<void | __esri.FeatureSet> {
  // const whereQuery = `DHS_CC = 'BF'`;
  const whereQuery = `DHS_CC = '${countryCode}'`;

  const queryParams = {
    where: whereQuery,
    returnGeometry: true,
    // outFields: ['*'],
    ...QUERY_PERFORMANCE_OPTIONS,
  };

  return await query.executeQueryJSON(SERVICES__GEOMETRY__BASEMAP_REGIONS_TEST__MAP_SERVER__URL, queryParams).then(
    function (results: __esri.FeatureSet) {
      return results;
    },
    function (error) {
      console.error(error);
    }
  );
}

/**
 * Get the selected features from the DHS Regions layer
 * @param {string} countryCode - Country code (e.g. 'BF')
 * @param {number} year - 4 digit year (e.g. 2021)
 * @returns {Promise<void | __esri.FeatureSet>} - Admin 1 features
 */
export async function getAdmin1FeaturesWithGeometries(
  countryCode: string,
  year: number
): Promise<void | __esri.FeatureSet> {
  // const whereQuery = `LEVELRNK = 1 AND DHSCC = 'BF' AND SVYYEAR = 2021`;
  const levelRank = getLevelRank(countryCode);
  const whereQuery = `LEVELRNK = ${levelRank} AND DHSCC = '${countryCode}' AND SVYYEAR = ${year}`;

  const queryParams = {
    where: whereQuery,
    outFields: ['*'],
    returnGeometry: true,
    ...QUERY_PERFORMANCE_OPTIONS,
  };

  return await query.executeQueryJSON(ADMIN_1_VECTOR_BOUNDARIES, queryParams).then(
    function (results: __esri.FeatureSet) {
      return results;
    },
    function (error) {
      console.error(error);
    }
  );
}

/**
 * Fetch the selected features from the DHS Admin 2 layer (boundaries)
 * @param {string} countryName - Country name (e.g. 'Burkina Faso')
 * @returns {Promise<void | __esri.FeatureSet>} - Admin 2 features
 */
export async function fetchAdmin2FeaturesWithGeometries(countryName: string): Promise<void | __esri.FeatureSet> {
  // const whereQuery = "ADM0_NAME = 'Burkina Faso'";
  const whereQuery = `ADM0_NAME = '${countryName}'`;

  const queryParams = {
    where: whereQuery,
    // outFields: ['*'],
    outFields: ['ADM2_NAME,ADM1_NAME,ADM0_NAME'],
    returnGeometry: true,
    ...QUERY_PERFORMANCE_OPTIONS,
  };

  return await query.executeQueryJSON(ADMIN_2_VECTOR_BOUNDARIES, queryParams).then(
    function (results: __esri.FeatureSet) {
      return results;
    },
    function (error) {
      console.error(error);
    }
  );
}

/**
 * Get the selected features from the DHS Regions layer
 * @param {string} countryCode - Country code (e.g. 'BF')
 * @param {string} surveyType - Survey type (e.g. 'DHS')
 * @returns {Promise<void | __esri.FeatureSet>} - Admin 2 features
 */
export async function getAdmin2FeaturesData(
  countryCode: string,
  surveyType: string
): Promise<void | __esri.FeatureSet> {
  const { allSurveys } = store.getState().admin2EstimatesSlice;

  if (allSurveys) {
    const admin2FeaturesDataFiltered = allSurveys.filter((survey: any) => {
      return (
        survey.attributes[ATTRIBUTE_FIELD_COUNTRY_CODE] === countryCode &&
        survey.attributes[ATTRIBUTE_FIELD_SURVEY_TYPE] === surveyType
      );
    });

    return admin2FeaturesDataFiltered;
  } else {
    // TODO: Remove this once the API is ready
    const whereQuery = `DHSCC = '${countryCode}' AND SurveyType = '${surveyType}'`;
    const queryParams = {
      where: whereQuery,
      outFields: ['*'],
    };

    const promise = store.dispatch(
      api.endpoints.getEsriRestServiceData.initiate({ url: ADMIN_2_VECTOR_DATA, queryParams: queryParams })
    );

    const { refetch } = promise;
    // interact with the cache in the same way as you would with a useFetch...() hook
    const promiseResolved = await promise;

    if (promiseResolved.isSuccess) {
      return promiseResolved.data;
    } else if (promiseResolved.isError) {
      console.error('getAdmin2FeaturesData failed', promiseResolved.error);
    }
  }

  // TODO: Remove this once the API is ready
  // return await query.executeQueryJSON(ADMIN_2_VECTOR_DATA, queryParams).then(
  //   function (results: __esri.FeatureSet) {
  //     const admin2FeaturesData = results.toJSON();
  //
  //     return admin2FeaturesData;
  //   },
  //   function (error) {
  //     console.error(error);
  //   }
  // );
}
